import { useEffect, useRef, useState } from 'react';
import type {
  ColumnOrderState,
  ColumnSizingState,
  OnChangeFn,
  VisibilityState,
} from '@/lib/tanstack-table';
import { useDebouncedCallback } from '@react-hookz/web';
import { z } from 'zod';
import { useTableFilterPreferences } from './useTableFilterPreferences';

/**
 * Persisted column layout: TanStack Table's own state shapes, stored as-is so
 * adopting a table is just spreading this hook's return into `useTable`.
 */
const ColumnLayoutSchema = z.object({
  columnOrder: z.array(z.string()).optional(),
  columnVisibility: z.record(z.string(), z.boolean()).optional(),
  columnSizing: z.record(z.string(), z.number()).optional(),
});

type ColumnLayout = z.infer<typeof ColumnLayoutSchema>;

/** Suffix keeps the layout row separate from the same table's filter prefs. */
const LAYOUT_TABLE_ID_SUFFIX = ':layout';

/**
 * Per-user, cross-session persistence for a table's column layout — order,
 * visibility, and widths — on top of the same generic per-table preference
 * store that already remembers filters. Reusable by any TanStack table: pass
 * a stable `tableId`, spread the returned `state` keys and `onChange`
 * handlers into `useTable`, and the layout follows the user across
 * sessions and devices with no backend or contract change.
 *
 * Writes are debounced because column resizing streams a state update per
 * mouse-move; only the settled layout is worth a round trip.
 */
export function useTableColumnSettings(tableId: string) {
  const {
    savedFilters: savedLayout,
    saveFilters: saveLayout,
    isPending,
  } = useTableFilterPreferences(
    `${tableId}${LAYOUT_TABLE_ID_SUFFIX}`,
    ColumnLayoutSchema,
  );

  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});

  // Seed local state from the saved row exactly once, when it arrives. An
  // edit the user makes before the fetch settles marks the state seeded
  // (every change handler below does), so a late-arriving saved layout never
  // clobbers a live edit. setState-during-render keeps the seed synchronous
  // with the same render that first sees the data.
  const [seeded, setSeeded] = useState(false);
  if (!seeded && !isPending) {
    setSeeded(true);
    if (savedLayout?.columnOrder) setColumnOrder(savedLayout.columnOrder);
    if (savedLayout?.columnVisibility) {
      setColumnVisibility(savedLayout.columnVisibility);
    }
    if (savedLayout?.columnSizing) setColumnSizing(savedLayout.columnSizing);
  }

  // The debounced persist cancels — not flushes — on unmount, so a change
  // made within the debounce window right before navigating away would be
  // lost. Track the latest unsaved layout and commit it from the unmount
  // cleanup instead. saveLayout is a fresh closure every render, so the
  // cleanup reads it through a ref.
  const pendingLayoutRef = useRef<ColumnLayout | null>(null);
  const saveLayoutRef = useRef(saveLayout);
  saveLayoutRef.current = saveLayout;
  useEffect(
    () => () => {
      if (pendingLayoutRef.current) {
        saveLayoutRef.current(pendingLayoutRef.current);
      }
    },
    [],
  );

  const persist = useDebouncedCallback(
    (layout: ColumnLayout) => {
      pendingLayoutRef.current = null;
      saveLayoutRef.current(layout);
    },
    [],
    500,
  );

  const queuePersist = (layout: ColumnLayout) => {
    setSeeded(true);
    pendingLayoutRef.current = layout;
    persist(layout);
  };

  const onColumnOrderChange: OnChangeFn<ColumnOrderState> = (updater) => {
    const next = typeof updater === 'function' ? updater(columnOrder) : updater;
    setColumnOrder(next);
    queuePersist({ columnOrder: next, columnVisibility, columnSizing });
  };

  const onColumnVisibilityChange: OnChangeFn<VisibilityState> = (updater) => {
    const next =
      typeof updater === 'function' ? updater(columnVisibility) : updater;
    setColumnVisibility(next);
    queuePersist({ columnOrder, columnVisibility: next, columnSizing });
  };

  const onColumnSizingChange: OnChangeFn<ColumnSizingState> = (updater) => {
    const next =
      typeof updater === 'function' ? updater(columnSizing) : updater;
    setColumnSizing(next);
    queuePersist({ columnOrder, columnVisibility, columnSizing: next });
  };

  const resetLayout = () => {
    setColumnOrder([]);
    setColumnVisibility({});
    setColumnSizing({});
    queuePersist({});
  };

  return {
    columnOrder,
    columnVisibility,
    columnSizing,
    onColumnOrderChange,
    onColumnVisibilityChange,
    onColumnSizingChange,
    resetLayout,
    isPending,
  };
}
